import './GroupActionButton.scss';
import React, { ReactElement, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { MdOutlineExpandMore } from 'react-icons/md';
import { IBaseAction } from '@models/actions/IBaseAction';
import { IActionService } from '@services/actions/IActionService';
import Menu from '@srm/ui/menu';
import Button from '@srm/ui/button';
import { IDocBasedService } from '@services/actions/IDocBasedService';
import { useLocation, useNavigate } from 'react-router';
import { convertActions } from '@utils/actionsHelpers';
import { useDocumentHashContext } from '@hooks/useDocumentHashContext';
import { useDraftMonitorContext } from '@hooks/useDraftMonitorContext';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { ConfigService } from '@/configuration/services/configService';
import { IDebugSettings } from '@models/IDebugSettings';
import GroupOperationFlow from '@organisms/actions/groupOperation/GroupOperationFlow/GroupOperationFlow';

export interface IGroupActionButtonRefActions {
    setObjData: (objIds: string, objData: unknown) => void;
}

export interface IGroupActionButtonProps {
    service?: IActionService<IBaseAction>;
    onModifyData?: () => void;
    controlRef: React.Ref<IGroupActionButtonRefActions>;
    /** Выделить строки списка — «Выделить неудачные» итога групповой операции */
    onSelectKeys?: (keys: string[]) => void;
}

/** Строки выбора по ключам — исполнителю, запущенному для части выбранных. */
const rowsOf = (objData: unknown, keys: string[]): unknown => {
    if (!Array.isArray(objData)) return objData;
    const wanted = new Set(keys);
    return objData.filter((row: unknown) => {
        const key = (row as { key?: unknown } | null)?.key;
        return typeof key !== 'string' || wanted.has(key);
    });
};

const GroupActionButton: React.FC<IGroupActionButtonProps> = (props: IGroupActionButtonProps) => {
    const { controlRef, onModifyData, onSelectKeys, service } = props;
    const config = ConfigService.get();
    const [debugSettings] = useLocalStorageState<IDebugSettings>('Debug-Settings', {
        notifyFormulaErrors: config.environment !== 'PROD',
        showActionKeys: config.environment !== 'PROD',
    });

    const navigate = useNavigate();
    const location = useLocation();

    useImperativeHandle(controlRef, () => ({
        setObjData: (objIds: string, objData: unknown) => {
            setObjIds(objIds);
            setObjData(objData);
        },
    }));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [objIds, setObjIds] = useState<string>('');
    const [objData, setObjData] = useState<unknown>();
    const [fetchedActions, setFetchedActions] = useState<IBaseAction[]>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [element, setElement] = useState<ReactElement>();
    const [groupRun, setGroupRun] = useState<{ action: IBaseAction; rowKeys: string[]; rows: unknown }>();
    const draftMonitor = useDraftMonitorContext();
    const docHashServ = useDocumentHashContext();

    useEffect(() => {
        setFetchedActions(undefined);
        setIsDialogOpen(false);
    }, [objIds]);

    /**
     * Запуск исполнителя действия для выбранных строк. `onComplete` — свой разбор итога (групповая операция по
     * объектам перечитывает список сама), без него успех перечитывает список.
     */
    const runExecutor = useCallback(
        (action: IBaseAction, ids: string, rows: unknown, onComplete?: (isSucceed: boolean) => void) => {
            const executor = service?.getActionExecutor(action);
            if (executor) {
                executor.navigate = navigate;
                executor.location = location;
            }

            const docsBasedService = service as unknown as IDocBasedService;
            let parentId: string | undefined = undefined;
            if (docsBasedService) {
                parentId = docsBasedService.documentId;
            }

            executor?.run(
                ids,
                parentId,
                action,
                rows,
                (isSucceed) => {
                    draftMonitor?.resume();
                    if (docHashServ) {
                        docHashServ.resumeMonitoring();
                    }

                    setElement(undefined);

                    if (onComplete) onComplete(isSucceed);
                    else if (isSucceed && onModifyData) {
                        onModifyData();
                    }
                },
                () => {
                    draftMonitor?.pause();
                    if (docHashServ) {
                        docHashServ.pause();
                    }

                    setElement(executor?.visualElement && executor?.visualElement());
                },
            );
            setElement(executor?.visualElement && executor?.visualElement());
        },
        [docHashServ, draftMonitor, location, navigate, onModifyData, service],
    );

    const onActionClick = useCallback(
        (action: IBaseAction) => {
            const rowKeys = objIds.split(',').filter((id) => id);
            // Кнопка с groupMode="perItem" над несколькими строками: план, выполнение по объектам и итог.
            // Без него — прежнее выполнение пачкой.
            if (action.groupMode === 'perItem' && rowKeys.length > 1 && service?.groupPlan) {
                setGroupRun({ action, rowKeys, rows: objData });
                return;
            }
            runExecutor(action, objIds, objData);
        },
        [objData, objIds, runExecutor, service],
    );

    const finishGroupRun = useCallback(() => setGroupRun(undefined), []);

    useEffect(() => {
        setFetchedActions(undefined);
        setIsDialogOpen(false);
    }, [debugSettings?.showActionKeys]);

    const actItems = useMemo(
        () => convertActions(fetchedActions, onActionClick, debugSettings.showActionKeys, config),
        [config, debugSettings.showActionKeys, fetchedActions, onActionClick],
    );

    const loadActions = useCallback(() => {
        if (fetchedActions || isLoading || !service) {
            return;
        }

        setIsLoading(true);

        service
            .fetchGroupActions(objIds)
            .then((res) => {
                setFetchedActions(res.data);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [fetchedActions, isLoading, objIds, service]);

    const onOpenChange = useCallback(
        (opened: boolean) => {
            setIsDialogOpen(opened);
            if (opened) {
                loadActions();
            }
        },
        [loadActions],
    );

    // Групповая операция живёт дольше выбора: удалённые строки снимают выбор, а итог должен остаться на экране.
    const groupRunElement = groupRun && service?.groupPlan && (
        <GroupOperationFlow
            action={groupRun.action}
            rowKeys={groupRun.rowKeys}
            source={service.groupPlan}
            runExecutor={(keys, onComplete) =>
                runExecutor(groupRun.action, keys.join(','), rowsOf(groupRun.rows, keys), onComplete)
            }
            onModified={onModifyData}
            onFinished={finishGroupRun}
            onSelectKeys={onSelectKeys}
        />
    );

    // Слоты постоянны: снятый выбор не размонтирует групповую операцию.
    return (
        <>
            {objIds.length > 0 && (
                <Menu
                    className="group-action-button"
                    list={actItems}
                    loading={isLoading}
                    opened={isDialogOpen}
                    onOpenChange={onOpenChange}
                    position="bottom-start"
                    width={320}
                    popoverProps={{ className: 'group-action-button__dialog', hoverEndAdornment: true }}
                >
                    <Button
                        variant="secondary"
                        tone="neutral"
                        size="sm"
                        aria-label="Действия"
                        startAdornment={<MdOutlineExpandMore size="20" />}
                    >
                        Действия
                    </Button>
                </Menu>
            )}

            {(objIds.length > 0 || groupRun) && element}
            {groupRunElement}
        </>
    );
};

export default GroupActionButton;
